<template>
  <v-content class="gb list-invited-users-all">
    <v-container fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" md="12" class="top-container">
          <v-card
            :loading="loading"
            class="fill-width table_theme card-theme custom-container"
          >
            <v-card-title class="title_table medium-font" style="letter-spacing: normal">
              Invited Users (All Companies)

              <v-chip small class="ml-3" color="#E3F2FD" text-color="#1565C0" style="font-weight: 600;">
                {{ invitedUsers.length }} total
              </v-chip>

              <v-progress-circular
                v-if="loading"
                indeterminate
                color="primary"
                style="padding: 0px 30px;"
              ></v-progress-circular>

              <v-spacer></v-spacer>
              <div class="custom-o-search-bar">
                <v-text-field
                  v-model="search"
                  placeholder="Search by name, phone, or company..."
                  single-line
                  hide-details
                  class="input_search"
                  color="#198beb"
                >
                  <img
                    :src="require('@/assets/img/search.svg')"
                    slot="prepend"
                    alt="search"
                    class="info-tooltip"
                  />
                </v-text-field>
              </div>
            </v-card-title>
            <v-divider class="ma-0 list-divider"></v-divider>

            <!-- Status stats bar -->
            <div class="status-stats-bar">
              <div class="status-stats-bar__item">
                <span class="status-stats-bar__label">Total Invited</span>
                <span class="status-stats-bar__count">{{ invitedUsers.length }}</span>
              </div>
              <div
                v-for="item in statusCounts"
                :key="item.status"
                style="display: flex; align-items: center;"
              >
                <div class="status-stats-bar__divider"></div>
                <div
                  class="status-stats-bar__item"
                  :class="{ 'status-stats-bar__item--active': filters.status === item.status }"
                  @click="toggleStatusFilter(item.status)"
                  style="cursor: pointer;"
                >
                  <v-icon class="status-stats-bar__dot" :style="{ color: statusColor(item.status) }">brightness_1</v-icon>
                  <span class="status-stats-bar__label">{{ item.status }}</span>
                  <span class="status-stats-bar__count">{{ item.count }}</span>
                </div>
              </div>
              <div class="status-stats-bar__actions">
                <v-select
                  v-model="filters.status"
                  :items="statusFilterOptions"
                  placeholder="Filter Status"
                  dense
                  hide-details
                  clearable
                  style="max-width: 180px; font-size: 13px;"
                  class="mr-3"
                ></v-select>
                <v-btn
                  rounded text color="#29ABE2"
                  class="btn-blanco table_btn custom-o-white btn-border action-btn-width"
                  style="text-transform: none;"
                  @click="exportToExcel"
                  :disabled="filteredItems.length === 0"
                >Download (Excel)</v-btn>
              </div>
            </div>

            <v-alert v-if="error" type="error" dense dismissible @input="error = false">
              {{ errorMessage }}
            </v-alert>

            <v-data-table
              :headers="headers"
              :items="filteredItems"
              :search="search"
              :custom-filter="searchFilter"
              color="#717e91"
              item-key="id"
              :options.sync="options"
              class="invited-users-table"
              :mobile-breakpoint="0"
            >
              <template v-slot:item.full_name="{ item }">
                <span class="cell-text">{{ (item.first_name + ' ' + item.last_name).trim() || '—' }}</span>
              </template>

              <template v-slot:item.phone="{ item }">
                <span class="cell-text">{{ item.phone || '—' }}</span>
              </template>

              <template v-slot:item.email="{ item }">
                <span class="cell-text">{{ item.email || '—' }}</span>
              </template>

              <template v-slot:item.referred_by_name="{ item }">
                <div v-if="item.referred_by_name">
                  <span class="cell-text">{{ item.referred_by_name }}</span>
                  <span v-if="item.referred_by_role" class="cell-subtext">{{ item.referred_by_role }}</span>
                </div>
                <span v-else class="cell-text">—</span>
              </template>

              <template v-slot:item.company_name="{ item }">
                <span class="cell-text">{{ item.company_name || '—' }}</span>
              </template>

              <template v-slot:item.sent_at="{ item }">
                <span class="cell-text">{{ formatDate(item.sent_at) }}</span>
              </template>

              <template v-slot:item.kiey_status="{ item }">
                <v-chip
                  small
                  :color="statusChipColor(item.kiey_status)"
                  :text-color="statusChipTextColor(item.kiey_status)"
                  style="font-weight: 600; font-size: 11px;"
                >
                  {{ item.kiey_status || 'Not Registered' }}
                </v-chip>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-content>
</template>

<script>
import axios from "axios";
import config from "@/config/constants.js";
import { auth } from "@/config/firebase";

export default {
  name: "InvitedUsersAll",
  data() {
    return {
      loading: false,
      error: false,
      errorMessage: "",
      search: "",
      invitedUsers: [],
      filters: {
        status: "",
      },
      options: {
        itemsPerPage: 25,
        sortBy: ["sent_at"],
        sortDesc: [true],
      },
      headers: [
        { text: "INVITED BY", value: "referred_by_name", sortable: true },
        { text: "COMPANY", value: "company_name", sortable: true },
        { text: "DATE SENT", value: "sent_at", sortable: true, width: "130px" },
        { text: "NAME", value: "full_name", sortable: true },
        { text: "PHONE", value: "phone", sortable: true, width: "150px" },
        { text: "EMAIL", value: "email", sortable: true },
        { text: "STATUS", value: "kiey_status", sortable: true, width: "140px" },
      ],
      statusFilterOptions: [
        "Active",
        "Signed Up",
        "Invited",
        "Onboarding",
        "Not Registered",
        "Deleted",
        "Cancelled",
      ],
    };
  },
  computed: {
    filteredItems() {
      let items = this.invitedUsers;
      if (this.filters.status) {
        items = items.filter(u => u.kiey_status === this.filters.status);
      }
      return items;
    },
    statusCounts() {
      const counts = {};
      this.invitedUsers.forEach(u => {
        const status = u.kiey_status || "Not Registered";
        counts[status] = (counts[status] || 0) + 1;
      });
      const order = ["Active", "Signed Up", "Invited", "Onboarding", "Not Registered", "Deleted", "Cancelled"];
      return order
        .filter(s => counts[s])
        .map(s => ({ status: s, count: counts[s] }));
    },
  },
  created() {
    this.$store.dispatch("Banner/show");
    this.fetchInvitedUsers();
  },
  methods: {
    async fetchInvitedUsers() {
      this.loading = true;
      this.error = false;
      try {
        // Use Firebase ID token (works pre-login and post-login)
        const headers = {};
        const firebaseUser = auth.currentUser;
        if (firebaseUser) {
          const idToken = await firebaseUser.getIdToken();
          headers['X-Firebase-Token'] = idToken;
        }

        const response = await axios.get(config.api_url + "web/invited-users-all", { headers });
        if (response.data.code === "SUCCESS") {
          this.invitedUsers = (response.data.data || []).map((item, idx) => ({
            ...item,
            id: idx,
            full_name: ((item.first_name || '') + ' ' + (item.last_name || '')).trim(),
          }));
        } else {
          this.error = true;
          this.errorMessage = response.data.error || "Failed to load invited users.";
        }
      } catch (err) {
        console.error("fetchInvitedUsers error:", err);
        this.error = true;
        this.errorMessage = "Failed to load invited users.";
      } finally {
        this.loading = false;
      }
    },

    searchFilter(value, search, item) {
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        (item.first_name || "").toLowerCase().includes(s) ||
        (item.last_name || "").toLowerCase().includes(s) ||
        (item.phone || "").toLowerCase().includes(s) ||
        (item.email || "").toLowerCase().includes(s) ||
        (item.referred_by_name || "").toLowerCase().includes(s) ||
        (item.company_name || "").toLowerCase().includes(s)
      );
    },

    statusColor(status) {
      switch (status) {
        case "Active": return "#4CAF50";
        case "Signed Up": return "#2196F3";
        case "Invited": return "#FF9800";
        case "Onboarding": return "#FF9800";
        case "Not Registered": return "#9E9E9E";
        case "Deleted": return "#F44336";
        case "Cancelled": return "#F44336";
        default: return "#9E9E9E";
      }
    },

    statusChipColor(status) {
      switch (status) {
        case "Active": return "#E8F5E9";
        case "Signed Up": return "#E3F2FD";
        case "Invited": return "#FFF3E0";
        case "Onboarding": return "#FFF3E0";
        case "Not Registered": return "#F5F5F5";
        case "Deleted": return "#FFEBEE";
        case "Cancelled": return "#FFEBEE";
        default: return "#F5F5F5";
      }
    },

    statusChipTextColor(status) {
      switch (status) {
        case "Active": return "#2E7D32";
        case "Signed Up": return "#1565C0";
        case "Invited": return "#E65100";
        case "Onboarding": return "#E65100";
        case "Not Registered": return "#616161";
        case "Deleted": return "#C62828";
        case "Cancelled": return "#C62828";
        default: return "#616161";
      }
    },

    toggleStatusFilter(status) {
      this.filters.status = this.filters.status === status ? "" : status;
    },

    formatDate(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },

    exportToExcel() {
      const items = this.filteredItems;
      if (!items.length) return;

      const headers = ["Name", "Phone", "Email", "Status", "Invited By", "Role", "Company", "Date Sent"];
      const rows = items.map(u => [
        ((u.first_name || '') + ' ' + (u.last_name || '')).trim(),
        u.phone || "",
        u.email || "",
        u.kiey_status || "Not Registered",
        u.referred_by_name || "",
        u.referred_by_role || "",
        u.company_name || "",
        this.formatDate(u.sent_at),
      ]);

      let csv = "\uFEFF" + headers.join(",") + "\n";
      rows.forEach(row => {
        csv += row.map(cell => '"' + String(cell).replace(/"/g, '""') + '"').join(",") + "\n";
      });

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "invited_users_all.csv";
      link.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style lang="scss" scoped>
.list-invited-users-all {
  .custom-container {
    max-width: 1222px !important;
    min-height: 400px;
    margin-bottom: 50px !important;
  }

  .cell-text {
    font-size: 13px;
    color: #333;
  }

  .cell-subtext {
    display: block;
    font-size: 11px;
    color: #94A3B8;
    text-transform: capitalize;
  }

  .status-stats-bar__item--active {
    background: #E3F2FD;
    border-radius: 6px;
    padding: 2px 8px;
  }
}
</style>
